import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GlobeIcon, MapPinIcon, ClockIcon, CheckIcon, WhatsAppIcon, PhoneIcon } from '../components/Icons';
import api from '../services/api';
import SEO from '../components/SEO';

const Packages = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, [selectedCategory]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const data = await api.getPackages(selectedCategory);
      setPackages(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError('Failed to load packages. Please try again later.');
      // Fallback to empty array on error
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };


  const categories = [
    { id: 'all', name: 'All Packages' },
    { id: 'domestic', name: 'Domestic' },
    { id: 'international', name: 'International' }
  ];

  return (
    <div className="flex-1">
      <SEO
        title="Tour Packages"
        description="Explore our range of tour packages across India and abroad. Domestic and international holidays, beach getaways, mountain adventures, and cultural tours at best prices."
        keywords="tour packages, India tours, holiday packages, domestic tours, international packages, vacation deals"
        canonical="/packages"
      />
      <section className="bg-gradient-to-r from-primary-blue to-dark-blue text-white py-16 md:py-20 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">Travel Packages</h1>
          <p className="text-xl md:text-2xl text-white/90">Discover amazing destinations with our curated travel packages</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map(category => (
              <button
                key={category.id}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                  selectedCategory === category.id
                    ? 'bg-primary-orange text-white shadow-lg'
                    : 'bg-white text-primary-blue border-2 border-primary-blue hover:bg-primary-blue hover:text-white'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">Loading packages...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-xl text-red-600">{error}</p>
            </div>
          ) : packages.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-gray-600">No packages found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {packages.map((pkg) => {
                return (
                <div 
                  key={pkg.id} 
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300"
                >
                  <Link to={`/packages/${pkg.slug || pkg.id}`} className="block">
                    {pkg.image_url ? (
                      <div className="h-48 w-full overflow-hidden">
                        <img 
                          src={pkg.image_url} 
                          alt={pkg.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                  <div className="bg-gradient-to-br from-light-blue to-light-orange p-12 text-center">
                    <div className="flex justify-center text-primary-blue">
                          <GlobeIcon className="w-20 h-20" />
                    </div>
                  </div>
                    )}
                  <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 hover:text-primary-orange transition-colors">{pkg.title}</h3>
                    <p className="text-primary-blue font-semibold mb-2 flex items-center gap-2">
                      <MapPinIcon className="w-4 h-4" />
                      {pkg.destination}
                    </p>
                    <p className="text-gray-600 text-sm mb-4 flex items-center gap-2">
                      <ClockIcon className="w-4 h-4" />
                      {pkg.duration}
                    </p>
                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">{pkg.description}</p>
                    
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <h4 className="text-primary-blue font-semibold mb-3">Highlights:</h4>
                      <ul className="space-y-2">
                          {pkg.highlights && pkg.highlights.slice(0, 3).map((highlight, index) => (
                          <li key={index} className="text-gray-600 text-sm flex items-center">
                            <CheckIcon className="text-primary-orange mr-2 w-4 h-4" />
                            {highlight}
                          </li>
                        ))}
                          {pkg.highlights && pkg.highlights.length > 3 && (
                            <li className="text-primary-blue text-sm font-medium">+ {pkg.highlights.length - 3} more</li>
                          )}
                      </ul>
                    </div>

                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
                      <span className="text-2xl font-bold text-primary-orange">{pkg.price}</span>
                        <span className="text-primary-blue font-semibold hover:text-primary-orange transition-colors">
                          View Details →
                        </span>
                      </div>
                    </div>
                  </Link>
                  <div className="px-6 pb-6">
                    <div className="flex gap-2 w-full">
                        <a 
                          href={`https://wa.me/917718838808?text=Hi, I'm interested in ${pkg.title} package to ${pkg.destination}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 p-3 bg-[#25d366] text-white rounded-lg font-semibold hover:bg-[#20ba5a] transition-all duration-300 flex items-center justify-center"
                          aria-label="Contact on WhatsApp"
                        >
                          <WhatsAppIcon className="w-5 h-5" />
                        </a>
                        <a 
                          href="tel:+917718838808"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 p-3 bg-primary-blue text-white rounded-lg font-semibold hover:bg-dark-blue transition-all duration-300 flex items-center justify-center"
                          aria-label="Call now"
                        >
                          <PhoneIcon className="w-5 h-5" />
                        </a>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-orange to-orange-600 text-white relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'/%3E%3C/svg%3E")`
          }}
        ></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">Can't Find What You're Looking For?</h2>
          <p className="text-xl md:text-2xl mb-8 text-white/90">Contact us for custom packages tailored to your needs</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917718838808" 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white text-primary-orange rounded-xl font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center gap-2">
                <WhatsAppIcon className="w-5 h-5" />
                Chat on WhatsApp
              </span>
            </a>
            <a 
              href="tel:+917718838808" 
              className="px-8 py-4 bg-white text-primary-orange rounded-xl font-semibold hover:bg-gray-100 border-2 border-white transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="flex items-center gap-2">
                <PhoneIcon className="w-5 h-5" />
                Call Us Now
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Packages;
